import { useCallback, useEffect, useRef, useState } from 'react';
import { useChannel } from '../useChannel';
import type { RealtimeEnvelope } from '../types';

/**
 * Phase 39-04: frontend wiring for the description-generation stream.
 *
 * Subscribes to the per-tenant/per-job ai-stream channel and reduces the
 * locked 4-event envelope into a simple `{status, text, error}` state the
 * AIDescriptionCard renders against.
 *
 * Wire shape (locked in PLAN.md §39-03 — matches ai-engine's
 * stream-publisher.ts):
 *   channel: `tenant.{tenantId}.ai-stream.{jobId}`
 *   events:
 *     - `started`  — { feature, started_at }
 *     - `token`    — { seq, delta }            (cumulative `seq` from 0)
 *     - `done`     — { full_text, total_tokens }
 *     - `failed`   — { error, code }
 *
 * Transitions:
 *   idle → started → streaming → done
 *                       ↓
 *                     failed
 *
 * Out-of-order handling (v1.7): we assume monotonically increasing `seq`.
 * The ai-engine publisher awaits each `publishToken` sequentially and
 * Centrifugo preserves publish order per channel, so reorders only happen
 * on reconnect-with-replay (which is NOT enabled on the ai-stream namespace
 * in v1.7 — no `history` config). If we ever see a delta with a seq less
 * than the highest we've already applied, we drop it (duplicate from
 * replay); if a seq gap appears, we still append but log a warning via the
 * `lastAppliedSeq` tracking. When Phase 40 turns on channel history, revisit
 * to buffer-and-reorder here.
 *
 * Final text authority: the `done` event's `full_text` is authoritative
 * (server-side accumulated). If `done` is lost we fall back to the
 * client-accumulated deltas, which matches what Centrifugo delivered anyway.
 */

export type DescriptionStreamStatus =
  | 'idle'
  | 'started'
  | 'streaming'
  | 'done'
  | 'failed';

export interface DescriptionStreamError {
  message: string;
  code: string;
  correlationId: string | null;  // Phase 41.3 — exposed from ai-engine bcee0cb0 envelope
}

export interface DescriptionStreamState {
  status:     DescriptionStreamStatus;
  /** Accumulated text from tokens (or final full_text once `done` fires). */
  text:       string;
  /** Number of tokens applied so far (used for progress UI). */
  tokenCount: number;
  error:      DescriptionStreamError | null;
  /** Call to clear state and return to `idle` so the hook is reusable on retry. */
  reset:      () => void;
}

// Discriminated union mirroring ai-engine/src/services/stream-publisher.ts
// — keep field names in sync if that file ever changes.
type StreamPayload =
  | { type: 'started'; feature: string; started_at: string }
  | { type: 'token';   seq: number; delta: string }
  | { type: 'done';    full_text: string; total_tokens: number }
  | { type: 'failed';  error: string; code: string; correlation_id?: string };

export function useDescriptionStream(
  tenantId: string | null | undefined,
  jobId:    string | null | undefined,
): DescriptionStreamState {
  const [status,     setStatus]     = useState<DescriptionStreamStatus>('idle');
  const [text,       setText]       = useState('');
  const [tokenCount, setTokenCount] = useState(0);
  const [error,      setError]      = useState<DescriptionStreamError | null>(null);

  // Track the highest seq we've applied, so replayed duplicates don't
  // re-accumulate into the text. Kept in a ref to avoid re-subscribing on
  // every token.
  const lastAppliedSeqRef = useRef<number>(-1);

  // When jobId/tenantId changes, wipe local state. Avoid carrying over the
  // previous job's partial text when the user clicks Generate again.
  useEffect(() => {
    setStatus('idle');
    setText('');
    setTokenCount(0);
    setError(null);
    lastAppliedSeqRef.current = -1;
  }, [tenantId, jobId]);

  const channel: string | null =
    tenantId && jobId ? `tenant.${tenantId}.ai-stream.${jobId}` : null;

  useChannel<StreamPayload>(channel, {
    onPublication: (env: RealtimeEnvelope<StreamPayload>) => {
      // centrifuge-js delivers whatever the backend publishes verbatim under
      // ctx.data. Our Laravel + ai-engine publishers wrap payloads in an
      // envelope {event, payload, broadcast_at}; the ai-engine
      // stream-publisher does NOT wrap — it publishes the raw StreamPayload
      // at the top level of data. Handle both shapes so we're robust to the
      // transport-side choice.
      //
      // The realtime-smoke.spec.ts (Phase 39-03) asserts events arrive as
      // `ctx.data.type` directly — confirming ai-engine's
      // centrifugo.publishForTenant publishes raw payloads, not envelopes.
      // Laravel broadcasts (CentrifugoBroadcaster) DO use the envelope shape.
      // For ai-stream, only the ai-engine publishes — so the "raw" branch is
      // the live path; the envelope branch is defense-in-depth in case a
      // future test smoke goes through Laravel.
      const raw = (env as unknown) as StreamPayload;
      const payload: StreamPayload | null =
        raw && typeof raw === 'object' && 'type' in raw
          ? raw
          : (env && env.payload ? env.payload : null);
      if (!payload) return;

      switch (payload.type) {
        case 'started': {
          setStatus('started');
          setError(null);
          return;
        }
        case 'token': {
          const seq   = payload.seq;
          const delta = payload.delta;
          // Strict-monotonic seq enforcement: drop duplicates (seq <= lastApplied)
          // so Centrifugo channel replay on reconnect doesn't double-append.
          // A gap (seq > lastApplied + 1) still appends — better to render
          // possibly-holey text than freeze waiting for a missing chunk.
          if (seq <= lastAppliedSeqRef.current) return;
          lastAppliedSeqRef.current = seq;
          setText((prev) => prev + delta);
          setTokenCount((n) => n + 1);
          setStatus((s) => (s === 'done' || s === 'failed' ? s : 'streaming'));
          return;
        }
        case 'done': {
          // full_text is authoritative — server-side accumulated. Replaces
          // our client-side accumulation to guarantee the Apply button
          // writes the exact text the trace + callback persisted.
          //
          // `ai-stream.{jobId}` receives TWO `done` events on the happy
          // path: the ai-engine stream-publisher emits the full payload
          // (`{type:'done', full_text, total_tokens}`) and then Laravel's
          // `AIJobStatus::broadcastTerminal` emits a bare marker
          // (`{type:'done'}`) without full_text. Without this guard, the
          // second event `setText(undefined)` would wipe the already-
          // streamed text and the next render would crash on
          // `stream.text.length`.
          if (typeof payload.full_text === 'string') {
            setText(payload.full_text);
          }
          setStatus('done');
          return;
        }
        case 'failed': {
          // Same duplicate-event concern as `done` above — Laravel's bare
          // `{type:'failed', error}` can race with the ai-engine's
          // richer `{type:'failed', error, code}`. Preserve whichever
          // arrives first by skipping the overwrite if we already have an
          // error recorded.
          setError((prev) => prev ?? {
            message: typeof payload.error === 'string' ? payload.error : 'Generation failed',
            code:    typeof payload.code  === 'string' ? payload.code  : 'unknown',
            correlationId:
              typeof (payload as { correlation_id?: unknown }).correlation_id === 'string'
                ? (payload as { correlation_id: string }).correlation_id
                : null,
          });
          setStatus('failed');
          return;
        }
      }
    },
  });

  const reset = useCallback(() => {
    setStatus('idle');
    setText('');
    setTokenCount(0);
    setError(null);
    lastAppliedSeqRef.current = -1;
  }, []);

  return { status, text, tokenCount, error, reset };
}
